import type { CountListItemDto, SiteDto } from '@pl/domain';
import { useMemo } from 'react';
import { useCounts, useSuggestedCounts } from './queries';

/*
 * A site's counts for the day, as the phone's count home and the office's Counts page both show
 * them: the day's five, filled by what is done, then what is being counted, then the suggestions.
 */

/** How many counts a site is asked for each day (spec §6.7). */
export const COUNTS_PER_DAY = 5;

/** The calendar day an instant falls on at a site, as YYYY-MM-DD: the site's wall clock decides "today". */
export const dayKey = (at: string | Date, timeZone: string) => new Date(at).toLocaleDateString('en-CA', { timeZone });

/** How far the site's clock is ahead of UTC at an instant, in ms (negative west of Greenwich). */
function offsetMs(at: Date, timeZone: string): number {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' })
      .formatToParts(at)
      .map((x) => [x.type, x.value]),
  );
  return Date.UTC(+p.year!, +p.month! - 1, +p.day!, +p.hour!, +p.minute!, +p.second!) - (at.getTime() - at.getUTCMilliseconds());
}

/**
 * The instant the site's current day began, on its own clock. Two passes: the offset at a first guess,
 * then at the corrected instant — they differ only when the day starts across a daylight-saving change.
 */
export function startOfSiteDay(timeZone: string, now: Date = new Date()): string {
  const [y, m, d] = dayKey(now, timeZone).split('-').map(Number) as [number, number, number];
  const midnightAsUtc = Date.UTC(y, m - 1, d);
  let at = midnightAsUtc;
  for (let i = 0; i < 2; i++) at = midnightAsUtc - offsetMs(new Date(at), timeZone);
  return new Date(at).toISOString();
}

export interface TodaysCounts {
  /** SKUs still to count today, in the server's order. */
  todo: string[];
  /** SKU counts finished today at this site, newest first, one per SKU. */
  done: CountListItemDto[];
  /** Counts started here and never finished, to be picked up again. */
  open: CountListItemDto[];
  /** Counts canceled today at this site, newest first: they take no slot, and their SKU is suggested again. */
  cancelled: CountListItemDto[];
  /** SKUs with a count open here: their earlier results are not shown while it is. */
  openSkus: Set<string>;
  /** `todo.length`: the number on the home tile. */
  left: number;
  total: number;
  suggested: ReturnType<typeof useSuggestedCounts>;
  /** Counts finished at this site since midnight on the site's clock. */
  counts: ReturnType<typeof useCounts>;
  /** Every open count at this site, however long ago it started. */
  opened: ReturnType<typeof useCounts>;
}

/**
 * Today's list, built from the server's suggestions and the counts already finished today.
 *
 * The suggestions alone cannot make it: they are ordered by when each SKU was last counted, so a SKU
 * finished this morning drops to the back and a new one takes its slot. "Suggestions minus today's
 * finished" would then read five left forever. So the day's five slots are filled by what is
 * already done first, and the suggestions (in the server's order, never re-ranked on the phone)
 * fill what remains. A SKU count still open takes a slot too: it is one of today's, just not done.
 *
 * Only the home and the count list use this, and they ask for the least the list needs: counts
 * finished since midnight on the site's clock (list items carry no expected, found or scanned), and
 * open counts on their own. Only the office asks for the open counts' tallies (`tally`).
 */
export function useTodaysCounts(
  site: SiteDto | undefined,
  /** `openPollMs`: how often to ask for the open counts; the count home, which draws how earlier counts went, asks every 10 s (I-1). */
  opts: { tally?: boolean; openPollMs?: number } = {},
): TodaysCounts {
  const suggested = useSuggestedCounts(site?.id);
  const day = site ? dayKey(new Date(), site.timezone) : '';
  // The same string all day, so the query keeps its key until the site's date changes.
  const since = useMemo(() => (site ? startOfSiteDay(site.timezone) : undefined), [day, site?.timezone]); // eslint-disable-line react-hooks/exhaustive-deps
  // By when they finished: a count started before the site's midnight and finished after it is today's.
  const counts = useCounts({ siteId: site?.id, finishedSince: since, limit: 100 }, Boolean(site?.id));
  // The office's Counts page shows each open count's tally; nothing on a phone asks for it (`tally`).
  const opened = useCounts({ siteId: site?.id, status: 'open', limit: 100, ...(opts.tally ? { include: 'scanned' as const } : {}) }, Boolean(site?.id), opts.openPollMs);

  const shaped = useMemo(() => {
    if (!site) return { todo: [], done: [], open: [], cancelled: [], openSkus: new Set<string>(), inProgress: 0 };
    const doneBySku = new Map<string, CountListItemDto>();
    for (const c of counts.data ?? []) {
      if (c.status !== 'finished' || c.scope !== 'sku' || !c.skuId || !c.finishedAt) continue;
      if (dayKey(c.finishedAt, site.timezone) !== day || doneBySku.has(c.skuId)) continue;
      doneBySku.set(c.skuId, c);
    }
    const open = (opened.data ?? []).filter((c) => c.status === 'open');
    const cancelled = (counts.data ?? []).filter((c) => c.status === 'cancelled' && c.finishedAt && dayKey(c.finishedAt, site.timezone) === day);
    const openSkus = new Set(open.filter((c) => c.scope === 'sku' && c.skuId).map((c) => c.skuId!));
    const inProgress = [...openSkus].filter((id) => !doneBySku.has(id)).length;
    const slots = Math.max(0, COUNTS_PER_DAY - doneBySku.size - inProgress);
    const todo = (suggested.data ?? [])
      .filter((row) => !doneBySku.has(row.skuId) && !openSkus.has(row.skuId))
      .slice(0, slots)
      .map((row) => row.skuId);
    return { todo, done: [...doneBySku.values()], open, cancelled, openSkus, inProgress };
  }, [counts.data, day, opened.data, site, suggested.data]);

  const { inProgress, ...rest } = shaped;
  return { ...rest, left: shaped.todo.length, total: shaped.todo.length + shaped.done.length + inProgress, suggested, counts, opened };
}
